import { AppEntity } from '@app/icare/db/schema/common'
import { msg } from '@lingui/core/macro'
import { entityId } from '@pkg/db/schema'
import { defineEnum } from '@pkg/shared/utils/enum.utils'
import { z } from 'zod'

export const UserRoleEnum = defineEnum({
  CARE_Aussendienst: msg`Außendienst`,
  CARE_Innendienst: msg`Innendienst`,
  CARE_Teamleitung: msg`Teamleitung`,
})

export type UserRoleType = typeof UserRoleEnum.$.Type

export const UserId = entityId('user')

export type UserIdType = typeof UserId.Type

export const UserSchema = AppEntity.extend({
  id: UserId.schema,
  email: z.string().transform(val => val.toLowerCase()),
  emailVerified: z.boolean().default(false),
  name: z.string(),
  image: z.string().nullish(),
  firstName: z.string().nullish(),
  lastName: z.string().max(255).min(1).nullish(),
  // All Entra CARE_* groups (role groups AND CARE_Team_* groups) — not just UserRoleEnum values.
  roles: z.array(z.string()),
  phone: z.string().nullish(),
  mobile: z.string().nullish(),
  fax: z.string().nullish(),
})

export type SyncableUser = z.infer<typeof UserSchema>

export const formatUserName = (val?: Pick<SyncableUser, 'firstName' | 'lastName' | 'name'> | null) => {
  const parts = [val?.firstName, val?.lastName].filter((part): part is string => Boolean(part?.trim()))
  return parts.length > 0 ? parts.join(' ') : undefined
}

export const formatUserInitials = (val?: SyncableUser | null) => {
  if (!val) {
    return undefined
  }
  const firstInitial = val.firstName?.charAt(0).toUpperCase()
  const lastInitial = val.lastName?.charAt(0).toUpperCase()
  if (!firstInitial || !lastInitial) {
    return undefined
  }

  return `${firstInitial}${lastInitial}`
}

export const isCareManager = (user?: Partial<SyncableUser> | null) =>
  user?.roles?.includes(UserRoleEnum.CARE_Aussendienst) ?? false
